import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  useTheme,
  alpha,
  Avatar
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../../core/hooks/useUserData";
import { RootState } from "../../../core/store/store";
import { TemplateResponse } from "../../../interfaces/template.interface";
import useTool from "./use-headline-pig";

type Props = {
  open: boolean;
  onClose: () => void;
  defaultNiche?: string;
};

type CreateJobData = {
  niche: string;
};


export const HeadlinePigCreateDialog = (props: Props) => {
  const { open = false, onClose, ...other } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [templates, setTemplates] = useState<Array<TemplateResponse>>([]);
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const navigate = useNavigate();
  const theme = useTheme();
  const tool = useTool();
  const userData = useUserData();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    formState,
  } = useForm<CreateJobData>({
    mode: "onChange",
    defaultValues: {
      niche: props.defaultNiche || "",
    },
  });


  const onSubmit: SubmitHandler<CreateJobData> = async (data) => {
    try {
      setLoading(true);
      await tool.createJob({ type: "HEADLINE", data }, navigate);
      await userData.getUser();
      toast.success("Generating headlines for your niche...");
    } catch (e: unknown) {
      const error = e as { response?: { data?: { error?: { message?: string } } }; request?: unknown };
      if (error.response?.data?.error?.message) {
        toast.error("Error: " + error.response.data.error.message);
      } else if (error.request) {
        toast.dismiss();
        toast.error("Network error occurred...");
      } else {
        toast.error("Error creating job");
      }
    }
    reset();
    setLoading(false);
    props.onClose();
  };


  return (
    <Dialog 
      fullWidth 
      maxWidth="sm" 
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main
              }}
            >
              <AutoAwesomeIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Generate Headlines
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Enter a niche to generate high-converting headlines
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            <Box>
              <TextField
                fullWidth
                label="Enter Your Niche"
                placeholder="e.g., Digital Marketing, Fitness, Personal Finance"
                disabled={loading}
                {...register("niche", { 
                  required: "Please enter a niche",
                  minLength: {
                    value: 2,
                    message: "Niche must be at least 2 characters"
                  }
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main,
                    }
                  }
                }}
              />
              {formState.errors.niche ? (
                <FormHelperText error={true}>
                  {formState.errors.niche.message}
                </FormHelperText>
              ) : (
                <FormHelperText>
                  Enter a topic or niche you want to create headlines for
                </FormHelperText>
              )}
            </Box>

            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            >
              <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                Pro Tip
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Be specific with your niche for better results. Instead of "Business", 
                try "SaaS Marketing" or "E-commerce Growth Strategies".
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            color="inherit" 
            onClick={onClose} 
            disabled={loading}
            sx={{
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[500], 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <LoadingButton 
            loading={loading} 
            type="submit" 
            variant="contained" 
            disabled={!formState.isValid || loading}
            startIcon={!loading && <AutoAwesomeIcon />}
            sx={{
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
              '&.Mui-disabled': {
                background: theme.palette.grey[300]
              }
            }}
          >
            Generate Headlines
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};